import { X } from "@phosphor-icons/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "flowbite-react";
import AnimatedModal from "../../../components/AnimatedModal";
import { formatNumber } from "../../../lib/utils";
import DeviceDisplay from "../../../components/DeviceLabel";
import { IOrder } from "../interface";


interface OrderDetailsModalProps {
  show: boolean;
  onClose: () => void;
  order: IOrder | null;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  show,
  onClose,
  order,
}) => {
  const { t } = useTranslation();

  if (!order) {
    return (
      <AnimatedModal show={show} onClose={onClose} size="xl" bodyClassName="p-0">
        <div className="flex flex-col items-center justify-center h-48 bg-white rounded-xl">
          <p className="text-gray-600 font-medium">{t("common.noDataFound")}</p>
        </div>
      </AnimatedModal>
    );
  }

  return (
    <AnimatedModal
      show={show}
      onClose={onClose}
      size="xl"
      closeButton={false}
      bodyClassName="p-0 overflow-hidden rounded-xl shadow-2xl border-none"
    >
      <div className="flex flex-col h-full bg-white">
        {/* Header Section */}
        <div className="bg-[#0f172a] p-6 relative">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold text-white tracking-tight">
              {order.order_id}
            </h2>
            <div className="flex items-center gap-2 text-gray-400">
              <span className="text-xs font-semibold">{t("orders.detail.modal.financialStatus")}:</span>
              <Badge 
                color={order.transaction_type?.toLowerCase() === "charged" ? "success" : "warning"} 
                size="xs" 
                className="font-bold rounded w-fit uppercase"
              >
                {order.transaction_type || "PENDING"}
              </Badge>
            </div>
          </div>
          <button
            className="absolute top-6 right-6 h-8 w-8 text-gray-400 hover:text-white transition-colors duration-200"
            onClick={onClose}
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column: Details */}
            <div className="space-y-8">
              {/* Product Info */}
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("orders.detail.modal.productName")}</p>
                <p className="text-base font-bold text-gray-900 leading-tight">
                  {order.product_title || "N/A"}
                </p>
              </div>

              {/* SKU Info */}
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("orders.detail.modal.skuBasePlan")}</p>
                <p className="text-sm font-semibold text-gray-700">
                  {order.sku_id || "N/A"}
                  {order.base_plan_id ? ` | ${order.base_plan_id}` : ""}
                </p>
              </div>

              {/* Marketing/Coupons */}
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("orders.detail.modal.marketingCoupons")}</p>
                <p className="text-sm font-semibold text-gray-600">
                  <span className="text-gray-400">{t("orders.detail.modal.promotionId")}: </span>
                  {order.promotion_id ? (
                    <span className="text-blue-600">
                      {order.promotion_id}
                      {order.discount_rate ? ` (Discount: ${order.discount_rate}%)` : ""}
                    </span>
                  ) : (
                    <span className="text-blue-600">N/A</span>
                  )}
                </p>
              </div>

              {/* Device Info */}
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("orders.detail.modal.deviceInfo")}</p>
                <DeviceDisplay
                  device={order.device_model}
                  showIcon
                  className="text-sm font-semibold text-gray-700"
                />
              </div>
            </div>

            {/* Right Column: Location & Financials */}
            <div className="space-y-8">
              {/* Location Details */}
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t("orders.detail.modal.locationDetails")}</p>
                <div className="flex items-start gap-1">
                  <p className="text-sm font-bold text-gray-800">
                    {order.buyer_city || "N/A"}, {order.buyer_state || ""} {order.buyer_postal_code || ""}
                  </p>
                  <span className="text-sm font-bold text-gray-600">
                    ({order.buyer_country || "N/A"})
                  </span>
                </div>
              </div>

              {/* Financial Breakdown Card */}
              <div className="bg-[#f8fafc] rounded-2xl p-6 border border-gray-100 space-y-4">
                <p className="text-[10px] font-bold text-primary-400 uppercase tracking-widest">{t("orders.detail.modal.financialBreakdown")}</p>

                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">{t("orders.detail.modal.itemPrice")}:</span>
                    <span className="text-gray-900 font-bold">
                      {formatNumber((order.amount_paid_buyer_currency || 0) - (order.amount_tax || 0))} {order.buyer_currency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">{t("orders.detail.modal.tax")}:</span>
                    <span className="text-gray-900 font-bold">
                      {formatNumber(order.amount_tax || 0)} {order.buyer_currency}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-base font-bold text-gray-800">{t("orders.detail.modal.finalCharged")}:</span>
                  <span className={`text-xl font-black ${(order.amount_paid_buyer_currency || 0) < 0 ? "text-red-500" : "text-secondary"}`}>
                    {formatNumber(order.amount_paid_buyer_currency || 0)} {order.buyer_currency}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default OrderDetailsModal;
